import type { Transition } from "@uirouter/react";
import type { RedirectToResult } from "@uirouter/react";

import { FiltersEnum } from "app/components/filters-bar/utils";
import { AccessWithoutMandatoryParamsHOC } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import {
    DisplayAdvertisingOverviewPage,
    PublisherAnalysisOverviewPage,
} from "app/pages/digital-marketing/ad-intelligence";
import {
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW,
    WEBSITE_ANALYSIS_ROOT,
} from "app/pages/sales-intelligence/constants/routes";
import { AdNetworksContainer } from "app/pages/website-analysis/traffic-sources/display-ads/ad-networks/AdNetworksContainer";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import {
    onRedirectWebsiteAnalysis,
    onRedirectWebsiteAnalysisWithTotalWebSource,
} from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectWebsiteAnalysis";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

const WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW_LEGACY = "accountreview_website_display_overview_legacy";
const WEBSITE_ANALYSIS_MARKETING_DISPLAY_PUBLISHERS = "accountreview_website_display_publishers";
const WEBSITE_ANALYSIS_MARKETING_DISPLAY_CREATIVE = "accountreview_website_display_creatives";
const WEBSITE_ANALYSIS_MARKETING_DISPLAY_VIDEOS = "accountreview_website_display_videos";

const redirectLegacyDisplayPathKeyToOverview =
    (): transitionHookFn =>
    (transition): RedirectToResult => {
        const params = transition.params();
        const { key, isWWW, country, duration, webSource } = params;

        return {
            state: WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
            params: {
                isWWW,
                country,
                duration,
                key,
                ...(webSource != null ? { webSource } : {}),
            },
        };
    };

export const DISPLAY_GROUP_CONFIG: Record<
    string,
    IRouterState & { isPdfDownloadButton?: boolean; pdfDownloadsMethod?: () => Promise<any> }
> = {
    [WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/publisher-overview/:isWWW/:country/:duration?key&webSource&adsViewType",
        component: AccessWithoutMandatoryParamsHOC(
            (props) => (
                <PublisherAnalysisOverviewPage
                    {...props}
                    isPreview={true}
                />
            ),
            "key",
            "website",
            {
                title: "publisher.analysis.preview.overview.empty.state.title",
                subtitle: "publisher.analysis.preview.overview.empty.state.subtitle",
            },
        ),
        configId: "AdIntelligenceWAOverview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "publisher",
        },
        trackingId: {
            section: "Account Review",
            subSection: "display",
            subSubSection: "publisherOverview",
        },
        pageTitle: "si.website-analysis.tab.publisher-overview",
        params: {
            ...filterConsistencyParams,
        },
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            country: 999,
            isWWW: "*",
        },
        isUSStatesSupported: true,
        hidePageTitle: true,
        resetOnStateChange: true,
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        homeState: "salesIntelligence-home",
        redirectTo: redirectPipe(onRedirectWebsiteAnalysisWithTotalWebSource()) as (
            transition: Transition,
        ) => RedirectToResult,
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/advertiser-overview/:isWWW/:country/:duration?key&webSource&adsViewType&adsSelectedSite&campaignsSelectedSite",
        component: AccessWithoutMandatoryParamsHOC(
            (props) => (
                <DisplayAdvertisingOverviewPage
                    {...props}
                    isPreview={true}
                />
            ),
            "key",
            "website",
        ),
        configId: "AdIntelligenceWAOverview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "display",
        },
        trackingId: {
            section: "Account Review",
            subSection: "display",
            subSubSection: "displayOverview",
        },
        pageTitle: "digitalsuite.analysis.display.overview.page.title",
        pageSubtitle: "analysis.display.overview.sub.title",
        params: {
            ...filterConsistencyParams,
        },
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            country: 999,
            isWWW: "*",
        },
        isUSStatesSupported: true,
        hidePageTitle: true,
        resetOnStateChange: true,
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        homeState: "salesIntelligence-home",
        redirectTo: redirectPipe(onRedirectWebsiteAnalysisWithTotalWebSource()) as (
            transition: Transition,
        ) => RedirectToResult,
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW_LEGACY]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW_LEGACY,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/display-overview/:key/:isWWW/:country/:duration?webSource",
        redirectTo: redirectLegacyDisplayPathKeyToOverview(),
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_PUBLISHERS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_PUBLISHERS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/display-publishers/:key/:isWWW/:country/:duration?webSource&sort&page&domain&industry&network",
        redirectTo: redirectLegacyDisplayPathKeyToOverview(),
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/display-ad-networks/:key/:isWWW/:country/:duration?webSource&sort&adNetwork&page",
        component: AdNetworksContainer,
        configId: "WebAnalysis",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "display",
        },
        trackingId: {
            section: "Account Review",
            subSection: "display",
            subSubSection: "displayMediators",
        },
        pageTitle: "analysis.display.mediators.title",
        pageSubtitle: "analysis.source.ads.tabs.mediators.tooltip",
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        params: {
            ...filterConsistencyParams,
        },
        defaultQueryParams: {
            webSource: "Desktop",
            duration: "3m",
            country: 999,
            isWWW: "*",
        },
        isUSStatesSupported: true,
        homeState: "salesIntelligence-home",
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_CREATIVE]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_CREATIVE,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/display-creatives/:key/:isWWW/:country/:duration?webSource&sort&domain",
        redirectTo: redirectLegacyDisplayPathKeyToOverview(),
    },
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_VIDEOS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_DISPLAY_VIDEOS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/display-videos/:key/:isWWW/:country/:duration?webSource&sort&domain",
        redirectTo: redirectLegacyDisplayPathKeyToOverview(),
    },
};
